import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { ICheckpointRange, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { CheckpointEditorFormModelNames, CheckpointType } from '@fi-shared/checklist/checklist-enum';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import {
    FiChecklistHelperService,
    IChecklistCheckpointPlaceholderLabelData,
} from '@fi-shared/checklist/fi-checklist-helper.service';
import { takeUntil } from 'rxjs/operators';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-checkpoint-edit-general-tab',
    template: `
        <fi-name-description-input
            [(ngModel)]="checkpoint.Names"
            [selectedLanguageId]="selectedLanguageId"
            [(mediaBankFileReferences)]="checkpoint.MediaBankFileReferences"
            [(descriptions)]="checkpoint.Descriptions"
            [namePlaceholderTextId]="labelData.namePlaceholderTextId"
            [namePlaceholderHintTextId]="labelData.namePlaceholderHintTextId"
            [descriptionPlaceholderHintTextId]="labelData.descriptionPlaceholderHintTextId"
            [showAttachmentsArea]="true"
            [objectType]="ObjectTypes.CheckPoint"
        />

        <fi-checkpoint-type-editor [(ngModel)]="checkpoint.Type" />

        @if (isMeasureUnitTypeVisible()) {
            <fi-checkpoint-measure-unit-editor [(ngModel)]="checkpoint.MeasureUnitType" />
        }

        <fi-checkpoint-ranges-editor
            [(checkpointRanges)]="checkpoint.Ranges"
            (checkpointRangesChange)="onCheckpointRangesChange($event)"
            [(checkpointConditions)]="checkpoint.Conditions"
            [checkpointType]="checkpoint.Type"
            [measureUnitType]="checkpoint.MeasureUnitType"
        />

        @if (isScoringEnabled()) {
            <fi-checkpoint-scoring-editor
                [(checkpointScoring)]="checkpoint.Scoring"
                [(checkpointScoringData)]="checkpoint.ScoringData"
            />
        }
    `,
    styleUrls: ['./general-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-edit-general-tab' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiCheckpointEditGeneralTabComponent }],
    standalone: false,
})
export class FiCheckpointEditGeneralTabComponent extends FiComponentsContainer implements OnInit, OnDestroy {
    @Input({ required: true }) isComplianceThresholdEnabled: boolean;
    @Input() focusInputName$: Subject<void>;

    @Input({ required: true }) checkpoint: ICheckpointV2;
    @Output() checkpointChange = new EventEmitter<ICheckpointV2>();
    @Output() checkpointRangesChange = new EventEmitter<ICheckpointRange[]>();
    @Output() checkpointNamesInitFinish = new EventEmitter<void>();

    labelData: IChecklistCheckpointPlaceholderLabelData;

    protected readonly ObjectTypes = ObjectTypes;

    constructor(
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private fiChecklistStoreService: FiChecklistStoreService,
        private fiChecklistHelperService: FiChecklistHelperService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    get selectedLanguageId(): number {
        return this.fiChecklistStoreService.selectedLanguageId;
    }

    ngOnInit(): void {
        this.addMissedLanguageLabels();
        this.labelData = this.fiChecklistHelperService.checkpointPlaceholderLabelData;
        this.fiChecklistStoreService.selectedLanguageIdChange$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => this.cdr.markForCheck());
    }

    onCheckpointRangesChange($event) {
        this.checkpointRangesChange.emit($event);
    }

    isMeasureUnitTypeVisible(): boolean {
        return this.checkpoint.Type === CheckpointType.Measurement;
    }

    isScoringEnabled() {
        return this.isComplianceThresholdEnabled;
    }

    private addMissedLanguageLabels() {
        this.fiContentLanguageSharedService.initializeContentLanguagesArray(
            this.checkpoint.Names,
            this.checkpoint.Names,
        );
        this.fiContentLanguageSharedService.initializeContentLanguagesArray(
            this.checkpoint.Descriptions,
            this.checkpoint.Descriptions,
        );
        this.checkpointNamesInitFinish.emit();
    }

    onDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    protected readonly CheckpointEditorFormModelNames = CheckpointEditorFormModelNames;
}
